import gql from 'graphql-tag';
import { useCallback } from 'react';
import {
	useApolloClient,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import type ApolloClient from 'apollo-client';

import { expValEquals } from '@confluence/feature-experiments';
import { useLocalActivityCache } from '@confluence/legacy-schema-bridge/entry-points/useLocalActivityCache.ts';

import { LocalActivityContentFragment } from './LocalActivityContentFragment.fragment.ts';
import type {
	DehydratedLocalActivityQuery as DehydratedResult,
	DehydratedLocalActivityQuery_localActivity as DehydratedEvent,
} from './__types__/DehydratedLocalActivityQuery.ts';
import type {
	LocalActivityQuery as HydratedResult,
	LocalActivityQuery_localActivity as LocalActivityEvent,
} from './__types__/LocalActivityQuery.ts';
import { LocalActivityAction } from './__types__/LocalActivityQuery.ts';

const MAX_EVENTS = 100;

// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- This `@client` is about to go away
const DehydratedLocalActivityQuery = gql`
	query DehydratedLocalActivityQuery {
		localActivity @client {
			id
			action
			timestamp
			content {
				id
				__typename
			}
			__typename
		}
	}
`;

export type LocalActivity = {
	viewedEvents: LocalActivityEvent[];
	deletedContentIds: Set<string>;
	markAsConsistent: (contentId: string) => void;
};

const contentIdsToBeEvicted = new Set<string>();
const isMarkedForEviction = (event: DehydratedEvent) =>
	contentIdsToBeEvicted.has(event.content.id || '');

type LocalActivityFilters = {
	spaceKey?: string;
};

type UseLocalActivityOptions = {
	filters?: LocalActivityFilters;
	skip?: boolean;
};

export const useLocalActivity = ({
	filters,
	skip,
}: UseLocalActivityOptions = {}): LocalActivity => {
	const apolloClient = useApolloClient();
	const dehydratedLocalActivities = useLocalActivityCache();
	const localActivities = [];
	// how to fetch multiple contents when given a list of ids from cache
	const { data } = useQuery<HydratedResult>(
		// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- This `@client` is about to go away
		gql`
			query LocalActivityQuery {
				localActivity @client {
					id
					action
					timestamp
					content {
						id
						title
						hasRestrictions
						hasInheritedRestrictions
						space {
							id
							key
						}
						properties(keys: ["emoji-title-published", "emoji-title-draft"]) {
							nodes {
								id
								value
								key
								version {
									number
								}
							}
						}
						__typename
					}
					__typename
				}
			}
		`,
		{
			fetchPolicy: 'cache-only',
			skip: skip || expValEquals('confluence_replace_apollo_client_schema', 'isEnabled', true),
			returnPartialData: true,
		},
	);

	if (expValEquals('confluence_replace_apollo_client_schema', 'isEnabled', true)) {
		for (const event of dehydratedLocalActivities) {
			const hydratedEvent = { ...event };
			try {
				if (event?.content?.id) {
					const contentFragment = apolloClient.readFragment({
						id: `Content:${event.content.id}`,
						fragment: LocalActivityContentFragment,
					});
					if (contentFragment) {
						hydratedEvent.content = {
							...hydratedEvent.content,
							...contentFragment,
						};
					}
				}
			} catch {
				// If the fragment does not exist, don't do anything this event will get filtered out
			}
			localActivities.push(hydratedEvent);
		}
	}
	/**
	 * Events which are marked as consistent for all backend APIs can safely be evicted.
	 * At the time of writing, there is only 1 supported backend API (Activity Platform),
	 * so we can immediately mark the event for eviction.
	 *
	 * This eviction is "lazy", meaning that the event is *marked* as evicted & filtered from the
	 * result set, but not actually removed from memory until `trackLocalActivity` is called.
	 */
	const markAsConsistent = useCallback((contentId: string) => {
		contentIdsToBeEvicted.add(contentId);
	}, []);

	const result: LocalActivity = {
		viewedEvents: [],
		deletedContentIds: new Set<string>(),
		markAsConsistent,
	};

	const allEvents = expValEquals('confluence_replace_apollo_client_schema', 'isEnabled', true)
		? localActivities
		: data?.localActivity || [];

	allEvents.forEach((event) => {
		if (!shouldIncludeEvent(event, filters)) {
			return;
		}

		if (event.action === LocalActivityAction.viewed) {
			result.viewedEvents.push(event);
		} else if (event.action === LocalActivityAction.deleted) {
			result.deletedContentIds.add(event.content.id || '');
		}
	});
	return result;
};

const shouldIncludeEvent = (event: LocalActivityEvent, filters?: LocalActivityFilters) => {
	// Filter events which will be evicted
	if (isMarkedForEviction(event)) {
		return false;
	}

	if (!filters) {
		return true;
	}

	return filters.spaceKey === event.content.space?.key;
};

type TrackOptions = {
	contentId: string;
	action: LocalActivityAction;
};

/**
 * In addition to tracking new events, this function is also responsible for:
 * 1. enforcing the MAX_EVENTS cap
 * 2. actually evicting events from cache after they have been marked for eviction
 */
export const trackLocalActivity = (
	{ contentId, action }: TrackOptions,
	apolloClient: ApolloClient<object>,
	setLocalActivity: any,
	getLocalActivity: any,
) => {
	/**
	 * The Content is dehydrated (i.e. only Content.id is present, not full Content)
	 * because we may not know all Content data at track time.
	 * The full Content will be hydrated from cache automatically when querying in useLocalActivity
	 */
	let dehydratedEvents: DehydratedEvent[] = [];
	try {
		if (expValEquals('confluence_replace_apollo_client_schema', 'isEnabled', true)) {
			dehydratedEvents =
				getLocalActivity()
					?.filter((e: any) => !isMarkedForEviction(e))
					.slice(0, MAX_EVENTS - 1) || [];
		} else {
			// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
			const result = apolloClient.readQuery<DehydratedResult>({
				query: DehydratedLocalActivityQuery,
			});
			dehydratedEvents =
				result?.localActivity.filter((e) => !isMarkedForEviction(e)).slice(0, MAX_EVENTS - 1) || [];
		}
	} catch {}

	contentIdsToBeEvicted.clear();

	const data: DehydratedResult = {
		localActivity: [
			{
				id: `${contentId}-${action}`,
				action,
				timestamp: new Date().toISOString(),
				content: {
					id: contentId,
					__typename: 'Content',
				},
				__typename: 'LocalActivityEvent',
			},
			...dehydratedEvents,
		],
	};
	if (expValEquals('confluence_replace_apollo_client_schema', 'isEnabled', true)) {
		setLocalActivity(data.localActivity);
	} else {
		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		apolloClient.writeQuery({
			query: DehydratedLocalActivityQuery,
			data,
		});
	}
};
